import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TRY, fmtDate } from "@/lib/format";
import { History, Plus, Pencil, Trash2, ArrowRight, Loader2, ListChecks } from "lucide-react";

const ACTION = {
  create: { label: "Oluşturuldu", icon: Plus, color: "bg-[#16A34A]/15 text-[#16A34A]" },
  update: { label: "Güncellendi", icon: Pencil, color: "bg-secondary/15 text-secondary" },
  delete: { label: "Silindi", icon: Trash2, color: "bg-destructive/15 text-destructive" },
};

const ITEM_CHANGE = {
  item_add: { tag: "Kalem eklendi", cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  item_remove: { tag: "Kalem çıkarıldı", cls: "bg-red-50 border-red-200 text-red-700" },
  item_change: { tag: "Kalem değişti", cls: "bg-amber-50 border-amber-200 text-amber-700" },
};

const MONEY = new Set(["subtotal", "discount", "net", "kdv", "total", "price", "cost", "list_price", "paid_amount", "grand_total", "kdv_total", "unit_price"]);
const PCT = new Set(["kdv_rate", "global_discount_rate", "discount_rate"]);
const DATE = new Set(["date", "valid_until", "due_date"]);
const VALUE_LABELS = {
  status: { taslak: "Taslak", gonderildi: "Gönderildi", degerlendiriliyor: "Değerlendiriliyor", kabul: "Kabul Edildi", red: "Reddedildi", bekliyor: "Bekliyor", siparis_verildi: "Sipariş Verildi", tamamlandi: "Tamamlandı" },
  payment_terms: { havale_kredi: "Havale / Kredi Kartı", havale_nakit: "Havale / Nakit", kredi_karti: "Kredi Kartı", cek: "Vadeli (Çek)" },
  payment_method: { nakit: "Nakit", kredi_karti: "Kredi Kartı", havale: "Havale/EFT", cek: "Çek" },
  type: { satis: "Satış", alis: "Alış", musteri: "Müşteri", tedarikci: "Tedarikçi", elektrikci: "Elektrikçi", her_ikisi: "Müşteri + Tedarikçi" },
  currency: { TL: "₺ TL", USD: "$ USD", EUR: "€ EUR" },
};
const fmtVal = (v, field) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Evet" : "Hayır";
  if (VALUE_LABELS[field] && VALUE_LABELS[field][v]) return VALUE_LABELS[field][v];
  if (DATE.has(field) && typeof v === "string") return fmtDate(v);
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "number") {
    if (MONEY.has(field)) return TRY(v);
    if (PCT.has(field)) return `%${v}`;
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  }
  return String(v);
};

const fullTime = (iso) => {
  try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

// Bir güncellemenin tek satırlık özeti: "Durum değişti · 2 alan · 1 kalem değişti"
const summarize = (changes = []) => {
  const fields = changes.filter((c) => !ITEM_CHANGE[c.field]);
  const items = changes.filter((c) => ITEM_CHANGE[c.field]);
  const parts = [];
  const st = fields.find((c) => c.field === "status");
  if (st) parts.push(`Durum: ${fmtVal(st.old, "status")} → ${fmtVal(st.new, "status")}`);
  const others = fields.filter((c) => c.field !== "status" && !["subtotal", "net", "kdv", "total", "discount"].includes(c.field));
  if (others.length) parts.push(others.map((c) => c.label).join(", ") + " değişti");
  const added = items.filter((c) => c.field === "item_add").length;
  const removed = items.filter((c) => c.field === "item_remove").length;
  const changed = items.filter((c) => c.field === "item_change").length;
  if (added) parts.push(`${added} kalem eklendi`);
  if (removed) parts.push(`${removed} kalem çıkarıldı`);
  if (changed) parts.push(`${changed} kalem değişti`);
  const tot = fields.find((c) => c.field === "total" || c.field === "grand_total");
  if (tot) parts.push(`Genel Toplam ${fmtVal(tot.old, "total")} → ${fmtVal(tot.new, "total")}`);
  return parts.join(" · ");
};

export function RecordHistory({ entityType, entityId, className = "" }) {
  const [logs, setLogs] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!entityId) return;
    setLogs(null); setErr(false);
    api.get(`/audit-logs?entity_type=${entityType}&entity_id=${entityId}&limit=100`)
      .then((r) => setLogs(r.data)).catch(() => { setErr(true); setLogs([]); });
  }, [entityType, entityId]);

  if (logs === null) {
    return <div className="flex items-center justify-center py-8 text-muted-foreground" data-testid="history-loading"><Loader2 className="h-4 w-4 animate-spin mr-2" />Geçmiş yükleniyor…</div>;
  }
  if (err) {
    return <div className="text-center py-8 text-sm text-destructive" data-testid="history-error">Geçmiş yüklenemedi. Lütfen tekrar deneyin.</div>;
  }
  if (logs.length === 0) {
    return <div className="text-center py-8 text-sm text-muted-foreground" data-testid="history-empty">Bu kayıt için değişiklik geçmişi bulunmuyor.<br />(Geçmiş yalnızca bu özellik eklendikten sonraki değişiklikleri gösterir.)</div>;
  }

  return (
    <div className={`relative border-l-2 border-border ml-3 space-y-3 ${className}`} data-testid="record-history">
      {logs.map((l) => {
        const a = ACTION[l.action] || ACTION.update;
        const Icon = a.icon;
        const changes = l.changes || [];
        const fieldChanges = changes.filter((c) => !ITEM_CHANGE[c.field]);
        const itemChanges = changes.filter((c) => ITEM_CHANGE[c.field]);
        const summary = l.action === "update" ? summarize(changes) : "";
        return (
          <div key={l.id} className="relative pl-6" data-testid={`history-${l.id}`}>
            <div className={`absolute -left-[13px] h-6 w-6 rounded-full flex items-center justify-center ${a.color} ring-4 ring-background`}>
              <Icon className="h-3 w-3" />
            </div>
            <div className="bg-card border rounded-lg p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-semibold text-sm">{a.label}{l.action === "update" && changes.length > 0 && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted rounded-full px-1.5 py-px">{changes.length} değişiklik</span>}</span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">{fullTime(l.created_at)}</span>
              </div>
              <div className="text-xs text-primary font-semibold mt-0.5">{l.user_name}</div>
              {summary && <div className="text-xs text-foreground/80 mt-1.5 flex items-start gap-1.5" data-testid="history-summary"><ListChecks className="h-3.5 w-3.5 text-secondary shrink-0 mt-px" /><span>{summary}</span></div>}
              {l.action === "create" && <div className="text-xs text-muted-foreground mt-1">Kayıt ilk kez oluşturuldu{l.entity_name ? ` · ${l.entity_name}` : ""}.</div>}
              {l.action === "delete" && <div className="text-xs text-muted-foreground mt-1">Kayıt silindi{l.entity_name ? ` · ${l.entity_name}` : ""}.</div>}
              {l.action === "update" && fieldChanges.length > 0 && (
                <div className="mt-2 rounded-md border overflow-hidden">
                  <div className="grid grid-cols-[1fr_1fr_auto_1fr] gap-x-2 px-2 py-1 bg-muted/60 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"><span>Alan</span><span>Eski</span><span /><span>Yeni</span></div>
                  {fieldChanges.map((c, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto_1fr] gap-x-2 items-center px-2 py-1 text-xs border-t" data-testid={`history-field-${c.field}`}>
                      <span className="font-semibold text-foreground truncate">{c.label}</span>
                      <span className="text-muted-foreground line-through truncate tabular-nums">{fmtVal(c.old, c.field)}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      <span className="text-foreground font-semibold truncate tabular-nums">{fmtVal(c.new, c.field)}</span>
                    </div>
                  ))}
                </div>
              )}
              {l.action === "update" && itemChanges.length > 0 && (
                <div className="mt-2 space-y-1">
                  {itemChanges.map((c, i) => {
                    const it = ITEM_CHANGE[c.field];
                    const name = String(c.label || "").split(" · ").slice(1).join(" · ");
                    return (
                      <div key={i} className={`text-xs rounded px-2 py-1.5 border ${it.cls}`} data-testid={`history-item-${c.field}`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-black uppercase tracking-wider">{it.tag}</span>
                          <span className="font-semibold text-foreground truncate">{name}</span>
                        </div>
                        {Array.isArray(c.detail) && c.detail.length > 0 ? (
                          <ul className="mt-1 space-y-0.5 text-foreground/90 tabular-nums" data-testid="history-item-detail">
                            {c.detail.map((d, k) => <li key={k} className="flex items-start gap-1"><span className="text-muted-foreground">•</span><span>{d}</span></li>)}
                          </ul>
                        ) : (
                          <div className="flex items-center gap-1.5 flex-wrap mt-0.5 tabular-nums">
                            {c.old != null && <span className={c.new != null ? "text-muted-foreground line-through" : "text-foreground"}>{fmtVal(c.old)}</span>}
                            {c.old != null && c.new != null && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                            {c.new != null && <span className="text-foreground font-medium">{fmtVal(c.new)}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { History };
